/**
 * /dashboard/intros — Buyer Activity Feed for breeders.
 *
 * Lists every accepted Verified Inquiry intro. Each row shows: buyer
 * name, breed interest, days since intro, nudges already sent, and
 * a single "Nudge" button that lights up at day 4 (first nudge) and
 * day 11 (second nudge). Once both are sent, the button switches to
 * "Move on" guidance.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HandshakeIcon as Handshake, EnvelopeSimpleIcon as EnvelopeSimple, ChatCircleDotsIcon as ChatCircleDots, ClockIcon as Clock, EyeIcon as Eye, XIcon as X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

function ageLabel(days) {
  if (days < 1) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function nudgeCta(eligibility) {
  if (!eligibility) return { label: "Nudge buyer", enabled: false, hint: "" };
  if (eligibility.next_nudge === "day_4") {
    return { label: "Nudge buyer · day-4 check-in", enabled: true, hint: "Recovers ~30% of stalled threads." };
  }
  if (eligibility.next_nudge === "day_11") {
    return { label: "Nudge buyer · 'still interested?'", enabled: true, hint: "Last gentle nudge before you move on." };
  }
  // No next nudge — either too early or both sent.
  const sentCount = (eligibility.nudges_sent || []).length;
  if (sentCount >= 2) {
    return { label: "Both nudges sent", enabled: false, hint: "Time to move on or re-list — buyer is unresponsive." };
  }
  if (eligibility.days_since < 4) {
    return {
      label: `Nudge available in ${4 - eligibility.days_since}d`,
      enabled: false,
      hint: "Give the buyer time to reply before the first nudge.",
    };
  }
  return {
    label: `Day-11 nudge in ${11 - eligibility.days_since}d`,
    enabled: false,
    hint: "First nudge is sent. Final nudge unlocks on day 11.",
  };
}

export default function BuyerActivityFeed() {
  const [intros, setIntros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);
  const [preview, setPreview] = useState(null); // { intro_id, kind, subject, html, to, reply_to } | null
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/kennel/intros");
      setIntros(r.data?.intros || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const nudge = async (introId) => {
    setNudging(introId);
    try {
      const { data } = await api.post(`/me/kennel/intros/${introId}/nudge`);
      toast.success(`Sent the ${data.nudge_kind === "day_4" ? "day-4" : "day-11"} nudge.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNudging(null);
    }
  };

  const openPreview = async (introId, kindHint) => {
    setPreviewLoading(true);
    try {
      const params = kindHint ? `?kind=${kindHint}` : "";
      const { data } = await api.get(`/me/kennel/intros/${introId}/nudge-preview${params}`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="buyer-activity-feed">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Buyer Activity Feed
        </p>
        <h1 className="font-spectral font-light text-4xl tracking-tight leading-[1.05] flex items-center gap-3">
          <Handshake size={28} weight="duotone" className="text-gold"/>
          Your accepted intros
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Every Verified Inquiry you accepted. Most buyers reply on day 1 — the
          ones who don&apos;t usually need one nudge to come back. Click &ldquo;Nudge&rdquo;
          and we send a soft check-in email on your behalf. Armstrong stays out
          of the negotiation.
        </p>

        {loading && (
          <div className="mt-8 text-sm text-[#6B6B6B]" data-testid="intros-loading">
            Loading your intros…
          </div>
        )}

        {!loading && intros.length === 0 && (
          <div className="mt-8 rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-8 text-center" data-testid="intros-empty">
            <ChatCircleDots size={28} weight="duotone" className="mx-auto text-gold mb-2"/>
            <p className="font-semibold">No accepted intros yet.</p>
            <p className="text-sm text-[#6B6B6B] mt-1">
              When a buyer pays the $9 Verified Inquiry fee and you click &ldquo;Accept&rdquo;
              from the email, the intro shows up here.
            </p>
            <Link to="/billing" className="inline-block mt-3 text-crimson underline font-bold text-[12px]">
              Back to billing →
            </Link>
          </div>
        )}

        <div className="mt-8 space-y-3">
          {intros.map((intro) => {
            const elig = intro.eligibility || {};
            const cta = nudgeCta(elig);
            const sentBadges = (elig.nudges_sent || []).map((k) =>
              k === "day_4" ? "Day-4 sent" : "Day-11 sent"
            );
            return (
              <div
                key={intro.id}
                className="rounded-xl border border-[#EAEAEA] bg-white p-5"
                data-testid={`intro-card-${intro.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <h3 className="font-spectral text-lg tracking-tight leading-tight">
                        {intro.lead?.buyer_name || "Buyer"}
                      </h3>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] inline-flex items-center gap-1">
                        <Clock size={11} weight="duotone"/> {ageLabel(elig.days_since || 0)}
                      </span>
                      {sentBadges.map((b) => (
                        <span
                          key={b}
                          className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00] bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                    <p className="text-[13px] text-[#3F3F3F] mt-1">
                      <strong>Breed interest:</strong> {intro.lead?.breed_interest || "—"}
                      {intro.lead?.buyer_city ? <> · {intro.lead.buyer_city}</> : null}
                    </p>
                    {intro.lead?.notes && (
                      <p className="text-[12.5px] text-[#6B6B6B] mt-2 line-clamp-2 leading-relaxed">
                        &ldquo;{intro.lead.notes}&rdquo;
                      </p>
                    )}
                    <p className="text-[11px] text-[#9A9A9A] mt-2 inline-flex items-center gap-1">
                      <EnvelopeSimple size={11} weight="duotone"/>
                      {intro.lead?.buyer_email}
                      <span className="ml-2">· intro #{intro.id.slice(0, 8)}</span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => openPreview(intro.id, elig.next_nudge)}
                        disabled={previewLoading}
                        variant="outline"
                        className="rounded-full h-10 px-3 text-[12px]"
                        title="Preview the email that will be sent"
                        data-testid={`intro-preview-${intro.id}`}
                      >
                        <Eye size={13} weight="duotone" className="mr-1"/>
                        Preview
                      </Button>
                      <Button
                        onClick={() => nudge(intro.id)}
                        disabled={!cta.enabled || nudging === intro.id}
                        className={`rounded-full h-10 px-4 ${
                          cta.enabled
                            ? "bg-ink hover:bg-crimson text-white"
                            : "bg-stone-100 text-stone-400 cursor-not-allowed"
                        }`}
                        data-testid={`intro-nudge-${intro.id}`}
                      >
                        {nudging === intro.id ? "Sending…" : cta.label}
                      </Button>
                    </div>
                    {cta.hint && (
                      <p className="text-[11px] text-[#9A9A9A] text-right max-w-[200px] leading-tight">
                        {cta.hint}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Side-drawer: preview of the next nudge email. Renders the
          actual templated HTML in an iframe so styles don't bleed
          into the dashboard. */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-stretch justify-end"
          data-testid="nudge-preview-drawer"
          onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <div className="absolute inset-0 bg-black/40"/>
          <div className="relative ml-auto w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#EAEAEA]">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-1">
                  Nudge preview · {preview.kind === "day_4" ? "Day 4" : "Day 11"}
                </p>
                <h2 className="font-spectral text-xl tracking-tight" data-testid="nudge-preview-subject">{preview.subject}</h2>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="rounded-full p-2 hover:bg-stone-100 transition-colors"
                aria-label="Close preview"
                data-testid="nudge-preview-close"
              >
                <X size={18} weight="bold"/>
              </button>
            </div>
            <div className="px-5 pt-3 pb-2 text-[12px] text-[#6B6B6B] border-b border-[#EAEAEA] bg-[#FAFAFA]">
              <div><strong>To:</strong> {preview.to}</div>
              <div><strong>Reply-To:</strong> {preview.reply_to}</div>
            </div>
            <iframe
              srcDoc={preview.html}
              title="Nudge email preview"
              className="flex-1 w-full border-0 bg-white"
              data-testid="nudge-preview-iframe"
              sandbox=""
            />
            <div className="p-5 border-t border-[#EAEAEA] flex items-center justify-between gap-3 bg-[#FAFAFA]">
              <p className="text-[11.5px] text-[#6B6B6B] leading-snug max-w-md">
                This is exactly what we&apos;ll send on your behalf. Hit Nudge to send now, or wait — the cron will send it automatically a day after the eligibility window opens.
              </p>
              <Button
                onClick={() => { const id = preview.intro_id; setPreview(null); nudge(id); }}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="nudge-preview-send"
              >
                Send now
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
